import { useEffect, useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '../Button'
import { Icon, type IconName } from '../Icon'
import css from './Overlay.module.css'

export type Tone = 'info' | 'warning' | 'danger' | 'success'

interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  tone?: Tone
  icon?: IconName
  size?: 'default' | 'wide' | 'full'
  children?: ReactNode
  footer?: ReactNode
  /** Menutup dengan klik latar. Dimatikan untuk aksi yang sedang berjalan. */
  dismissable?: boolean
}

const SELECTOR_FOKUS =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Dialog modal.
 *
 * Antarmuka lama memakai div biasa tanpa peran ARIA, tanpa perangkap fokus,
 * dan tanpa dukungan tombol Escape — pengguna papan tik bisa berpindah ke
 * elemen di belakang dialog yang sedang terbuka, dan pembaca layar tidak
 * pernah tahu ada dialog yang muncul.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  tone = 'info',
  icon,
  size = 'default',
  children,
  footer,
  dismissable = true,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const pemicuRef = useRef<HTMLElement | null>(null)
  const titleId = useId()
  const descId = useId()

  useEffect(() => {
    if (!open) return

    // Ingat elemen yang tadinya berfokus, agar fokus bisa dikembalikan ke sana
    // saat dialog ditutup — tanpa ini, pengguna papan tik terlempar ke awal
    // halaman setiap kali menutup dialog.
    pemicuRef.current = document.activeElement as HTMLElement | null

    // Kunci gulir halaman di belakang dialog. padding-right menggantikan lebar
    // batang gulir yang hilang, supaya tata letak tidak melompat.
    const lebarBatang = window.innerWidth - document.documentElement.clientWidth
    const gayaAsli = document.body.style.cssText
    document.body.style.overflow = 'hidden'
    if (lebarBatang > 0) document.body.style.paddingRight = `${lebarBatang}px`

    const fokusPertama = () => {
      const panel = panelRef.current
      if (!panel) return
      const kandidat = panel.querySelectorAll<HTMLElement>(SELECTOR_FOKUS)
      // Elemen terakhir biasanya tombol aksi utama; elemen pertama sering
      // tombol tutup. Bidang isian didahulukan bila ada.
      const isian = panel.querySelector<HTMLElement>('input, textarea, select')
      ;(isian ?? kandidat[0] ?? panel).focus()
    }
    const timer = setTimeout(fokusPertama, 0)

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissable) {
        e.preventDefault()
        onClose()
        return
      }

      if (e.key !== 'Tab') return

      // Perangkap fokus: Tab tidak boleh membawa pengguna keluar dari dialog.
      const panel = panelRef.current
      if (!panel) return
      const kandidat = Array.from(panel.querySelectorAll<HTMLElement>(SELECTOR_FOKUS)).filter(
        (el) => el.offsetParent !== null,
      )
      if (kandidat.length === 0) return

      const pertama = kandidat[0] as HTMLElement
      const terakhir = kandidat[kandidat.length - 1] as HTMLElement

      if (e.shiftKey && document.activeElement === pertama) {
        e.preventDefault()
        terakhir.focus()
      } else if (!e.shiftKey && document.activeElement === terakhir) {
        e.preventDefault()
        pertama.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.cssText = gayaAsli
      pemicuRef.current?.focus?.()
    }
  }, [open, dismissable, onClose])

  if (!open) return null

  const kelasPanel = [css.panel, size === 'wide' ? css.wide : '', size === 'full' ? css.full : '']
    .filter(Boolean)
    .join(' ')

  return createPortal(
    <div
      className={css.backdrop}
      onMouseDown={(e) => {
        // mousedown, bukan click: kalau pengguna mulai menyeret teks dari
        // dalam panel lalu melepas di latar, dialog tidak ikut tertutup.
        if (dismissable && e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={panelRef}
        className={kelasPanel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
      >
        <div className={css.header}>
          {icon && (
            <div className={`${css.headerIcon} ${css[tone]}`}>
              <Icon name={icon} size={20} />
            </div>
          )}
          <div className={css.headerText}>
            <h2 className={css.title} id={titleId}>
              {title}
            </h2>
            {description && (
              <p className={css.description} id={descId}>
                {description}
              </p>
            )}
          </div>
          {dismissable && (
            <Button
              variant="ghost"
              size="sm"
              icon="close"
              className={css.closeButton}
              onClick={onClose}
              aria-label="Tutup"
            />
          )}
        </div>

        {children && <div className={css.body}>{children}</div>}
        {footer && <div className={css.footer}>{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}
